/**
 * @file <bk-product-recommendations>: related or complementary products (sections/product-recommendations.liquid).
 *
 * Shopify only fills `recommendations` when the section is requested from the recommendations
 * endpoint, so the element renders empty on the product page and loads
 * `data-recommendations-url` (`<root>recommendations/products?product_id=…&limit=…&intent=…&section_id=…`)
 * once it comes within 600 px of the viewport. When Shopify returns products, the server-rendered
 * list replaces the element's content and the section is shown; when it returns none, the section
 * stays hidden, so a product never shows an empty "You may also like" row.
 */
class BkProductRecommendations extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #request = null;

  connectedCallback() {
    // Server markup that already holds the list (for example in the theme editor) needs no fetch.
    if (this.querySelector('[data-recommendations-list]')) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer?.disconnect();
        this.#observer = null;
        this.#load();
      },
      { rootMargin: '0px 0px 600px 0px' },
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#request?.abort();
    this.#request = null;
  }

  async #load() {
    const url = this.dataset.recommendationsUrl;
    if (!url) return;
    this.#request = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#request.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = html.querySelector('bk-product-recommendations');
      if (!source || !source.querySelector('[data-recommendations-list] > *')) {
        this.#hide();
        return;
      }
      this.replaceChildren(...[...source.childNodes].map((node) => document.importNode(node, true)));
      // Scripts parsed by DOMParser never run: re-create them (a module URL runs once per page).
      for (const inert of this.querySelectorAll('script[src]')) inert.remove();
      for (const script of html.querySelectorAll('script[src]')) this.#loadScript(script.getAttribute('src') || '');
      this.setAttribute('data-filled', '');
      this.hidden = false;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#hide();
    }
  }

  #hide() {
    this.hidden = true;
    this.removeAttribute('data-filled');
  }

  /**
   * Module scripts of the cards (quick add, quick view): a fresh element per URL; the browser runs a
   * module URL only once per page.
   * @param {string} src
   */
  #loadScript(src) {
    if (!src) return;
    const script = document.createElement('script');
    script.type = 'module';
    script.src = src;
    document.head.append(script);
  }
}

if (!customElements.get('bk-product-recommendations')) {
  customElements.define('bk-product-recommendations', BkProductRecommendations);
}
